/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildDraftKey,
  clearDraft,
  readDraft,
  sanitizeDraftData,
  writeDraft,
  type DraftScope,
  type StoredDraft,
} from '../utils/form-draft-store';

export const DRAFT_DEBOUNCE_MS = 1500;

export interface UseFormDraftOptions<T extends Record<string, unknown>> {
  scope: DraftScope;
  /** The values the form opened with; a form equal to them is never stored. */
  initial: T;
  /** True while the form is open: nothing is read or written otherwise. */
  active: boolean;
  /** The server record's `updated_at`; a newer one invalidates the draft. */
  serverUpdatedAt?: string | null;
  ttlMs?: number;
  debounceMs?: number;
  /** The form's live values, to avoid offering a draft the form already holds. */
  getCurrent?: () => T;
}

interface Pending<T> {
  key: string;
  values: T;
  initial: T;
  baseUpdatedAt?: string | null;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Order-independent JSON of the sanitised values, for equality checks.
 *
 * @param value - The values.
 * @returns A stable string.
 */
function stable(value: Record<string, unknown>): string {
  const sorted = (_k: string, v: unknown) => (v && typeof v === 'object' && !Array.isArray(v)
    ? Object.fromEntries(Object.entries(v as object).sort(([a], [b]) => a.localeCompare(b)))
    : v);
  return JSON.stringify(sanitizeDraftData(value), sorted);
}

/**
 * Whether two value sets are equal once sanitised (key order ignored).
 *
 * @param a - First values.
 * @param b - Second values.
 * @returns True when equal.
 */
function isSame(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  return stable(a) === stable(b);
}

/**
 * Debounced local draft of a form with a restore/discard offer.
 *
 * @param options - Scope, initial values, open state and server version.
 * @returns The offer plus schedule/flush/restore/discard/clear actions.
 */
export function useFormDraft<T extends Record<string, unknown>>(options: UseFormDraftOptions<T>) {
  const { scope, active, serverUpdatedAt, ttlMs, debounceMs = DRAFT_DEBOUNCE_MS } = options;
  const key = buildDraftKey(scope);
  const [offer, setOffer] = useState<StoredDraft<T> | null>(null);
  const live = useRef({ options, key, offer });
  live.current = { options, key, offer };
  const pending = useRef<Pending<T> | null>(null);

  const cancelPending = useCallback(() => {
    if (pending.current) clearTimeout(pending.current.timer);
    pending.current = null;
  }, []);

  const writePending = useCallback((p: Pending<T>): boolean => {
    if (isSame(p.values, p.initial)) return false;
    return writeDraft(p.key, p.values, { baseUpdatedAt: p.baseUpdatedAt });
  }, []);

  const flushPending = useCallback(() => {
    const p = pending.current;
    cancelPending();
    if (p) writePending(p);
  }, [cancelPending, writePending]);

  useEffect(() => {
    if (!active || !key) {
      setOffer(null);
      return undefined;
    }
    const { options: o } = live.current;
    const found = readDraft<T>(key, { ttlMs, serverUpdatedAt });
    setOffer(found && !isSame(found.data, o.initial) && !isSame(found.data, o.getCurrent?.() ?? o.initial) ? found : null);
    return flushPending;
  }, [active, key, ttlMs, serverUpdatedAt, flushPending]);

  const schedule = useCallback((values: T) => {
    const { options: o, key: k, offer: pendingOffer } = live.current;
    if (!o.active || !k || pendingOffer) return; // an unanswered offer is never overwritten
    cancelPending();
    const snapshot = { key: k, values, initial: o.initial, baseUpdatedAt: o.serverUpdatedAt };
    const timer = setTimeout(() => {
      pending.current = null;
      writePending({ ...snapshot, timer });
    }, debounceMs);
    pending.current = { ...snapshot, timer };
  }, [cancelPending, writePending, debounceMs]);

  const flush = useCallback((values: T): boolean => {
    const { options: o, key: k } = live.current;
    cancelPending();
    if (!k) return false;
    setOffer(null);
    return writePending({ key: k, values, initial: o.initial, baseUpdatedAt: o.serverUpdatedAt, timer: 0 as never });
  }, [cancelPending, writePending]);

  const clear = useCallback(() => {
    cancelPending();
    clearDraft(live.current.key);
    setOffer(null);
  }, [cancelPending]);

  const restore = useCallback((): T | null => {
    const data = live.current.offer?.data ?? null;
    setOffer(null);
    return data;
  }, []);

  return { offer, schedule, flush, clear, restore, discard: clear };
}
